import { Illustration, IllustrationLayout, IllustrationType } from '@core/components';
import { formatShortDate } from '@core/utils/dateUtils';
import { Card, Heading, Shimmer } from '@soteria/design-system-ve';

import * as styles from './CaseSearchResults.css';

export interface CaseSearchResultsProps {
  hasSearched: boolean;
  isLoading: boolean;
  results?: Array<Record<string, unknown>>;
  validationMessage?: string;
  featureInProgressMessage?: string;
}

/**
 * Empty state component
 */
function EmptyState({ hasSearched, isLoading }: { hasSearched: boolean; isLoading: boolean }) {
  if (isLoading) {
    return (
      <Card>
        <Shimmer className={styles.loadingShimmer} />
      </Card>
    );
  }

  return (
    <Card>
      <Illustration
        description={
          hasSearched ? "We couldn't find any cases matching your search criteria. Try adjusting your filters." : 'Use the filters above to search for cases.'
        }
        layout={IllustrationLayout.HORIZONTAL}
        title={hasSearched ? 'No results found' : 'Start your search'}
        type={IllustrationType.EMPTY}
      />
    </Card>
  );
}

/**
 * Message state component for validation or feature in progress
 */
function MessageState({ message }: { message: string }) {
  return (
    <Card>
      <Illustration description={message} layout={IllustrationLayout.HORIZONTAL} title="Notice" type={IllustrationType.EMPTY} />
    </Card>
  );
}

/**
 * Case card component for displaying individual case results
 */
function CaseCard({ result }: { result: Record<string, unknown> }) {
  // Result is the unwrapped data from CaseSearchResultItem
  const data = result;

  // Safely extract fields with fallbacks
  const userId = (data.userId ?? 'Unknown') as string;
  const totalStrikePoints = (data.totalStrikePoints ?? 0) as number;
  const rollingWindowDays = (data.rollingWindowDays ?? 30) as number;
  const windowStart = data.windowStart as string | undefined;
  const windowEnd = data.windowEnd as string | undefined;

  // Format dates using common utility
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return formatShortDate(dateString);
    } catch {
      return dateString;
    }
  };

  return (
    <Card>
      <div className={styles.caseCard}>
        {/* Main content row with User ID and Strike Score */}
        <div className={styles.caseMainRow}>
          <div className={styles.caseField}>
            <div className={styles.caseLabel}>User ID</div>
            <div className={styles.caseValue}>{userId}</div>
          </div>
          <div className={styles.caseField}>
            <div className={styles.caseLabel}>Total Strike Score</div>
            <div className={styles.caseValue}>{totalStrikePoints}</div>
          </div>
        </div>

        {/* Additional metadata row */}
        <div className={styles.caseMetadataRow}>
          <div className={styles.caseMetadataItem}>
            <span className={styles.caseMetadataLabel}>Rolling Window:</span>
            <span>{rollingWindowDays} days</span>
          </div>
          {windowStart && windowEnd && (
            <div className={styles.caseMetadataItem}>
              <span className={styles.caseMetadataLabel}>Window Period:</span>
              <span>
                {formatDate(windowStart)} - {formatDate(windowEnd)}
              </span>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

/**
 * Component for rendering case search results
 */
export function CaseSearchResults({ hasSearched, isLoading, results, validationMessage, featureInProgressMessage }: CaseSearchResultsProps) {
  const hasResults = results && results.length > 0;

  return (
    <div>
      {/* Results Header */}
      <div className={styles.resultsHeader}>
        <div className={styles.resultsHeaderContent}>
          <Heading className={styles.resultsTitle} level="h2">
            Results
          </Heading>
        </div>
        {/* Results controls will be added here when we have actual results */}
      </div>

      {/* Results Content or States */}
      {validationMessage ? (
        <MessageState message={validationMessage} />
      ) : featureInProgressMessage ? (
        <MessageState message={featureInProgressMessage} />
      ) : hasResults ? (
        <div className={styles.resultsContainer}>
          {results.map(result => (
            <CaseCard key={(result.userId as string) || JSON.stringify(result)} result={result} />
          ))}
        </div>
      ) : (
        <EmptyState hasSearched={hasSearched} isLoading={isLoading} />
      )}
    </div>
  );
}
